//@ts-nocheck
import { useEffect, useState } from "react";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import { CheckIcon } from "lucide-react";
import { ChevronDownIcon } from "../icons";
import { BracketIcon, DisabledIcon, RequiredIcon } from "../icons/Icons";
import {
  fetchJobApplication,
  updateJobApplication,
} from "../../features/jobApplication/JobApplicationSlice";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { toast } from "sonner";
import EvalutionApplicationQuestionSet from "./EvalutionApplicationQuestionSet";
import { useJobApplicationContext } from "../application/JobApplicationContext";

function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

const nameData = [
  { id: 1, name: "required", icon: <RequiredIcon /> },
  { id: 2, name: "optional", icon: <BracketIcon /> },
  { id: 3, name: "disabled", icon: <DisabledIcon /> },
];

const emailData = [
  { id: 1, name: "required", icon: <RequiredIcon /> },
  { id: 2, name: "optional", icon: <BracketIcon /> },
  { id: 3, name: "disabled", icon: <DisabledIcon /> },
];
const phoneData = [
  { id: 1, name: "required", icon: <RequiredIcon /> },
  { id: 2, name: "optional", icon: <BracketIcon /> },
  { id: 3, name: "disabled", icon: <DisabledIcon /> },
];
const locationData = [
  { id: 1, name: "required", icon: <RequiredIcon /> },
  { id: 2, name: "optional", icon: <BracketIcon /> },
  { id: 3, name: "disabled", icon: <DisabledIcon /> },
];
const resumeData = [
  { id: 1, name: "required", icon: <RequiredIcon /> },
  { id: 2, name: "optional", icon: <BracketIcon /> },
  { id: 3, name: "disabled", icon: <DisabledIcon /> },
];
const coverData = [
  { id: 1, name: "required", icon: <RequiredIcon /> },
  { id: 2, name: "optional", icon: <BracketIcon /> },
  { id: 3, name: "disabled", icon: <DisabledIcon /> },
];
const educationData = [
  { id: 1, name: "required", icon: <RequiredIcon /> },
  { id: 2, name: "optional", icon: <BracketIcon /> },
  { id: 3, name: "disabled", icon: <DisabledIcon /> },
];
const experienceData = [
  { id: 1, name: "required", icon: <RequiredIcon /> },
  { id: 2, name: "optional", icon: <BracketIcon /> },
  { id: 3, name: "disabled", icon: <DisabledIcon /> },
];

const ProfileSummaryData = [
  { id: 1, name: "required", icon: <RequiredIcon /> },
  { id: 2, name: "optional", icon: <BracketIcon /> },
  { id: 3, name: "disabled", icon: <DisabledIcon /> },
];

const EvaluationApplication = () => {
  const [activeSection, setActiveSection] = useState<string>("");
  const [name, setName] = useState(nameData[0]);
  const [email, setEmail] = useState(emailData[0]);
  const [phoneNumber, setPhoneNumber] = useState(phoneData[0]);
  const [location, setLocation] = useState(locationData[0]);
  const [resume, setResume] = useState(resumeData[0]);
  const [coverLetter, setCoverLetter] = useState(coverData[0]);
  const [educationDetails, setEducationDetails] = useState(educationData[0]);
  const [workExperience, setWorkExperience] = useState(experienceData[0]);
  const { jobId } = useParams<{ jobId: string }>();
  const dispatch = useAppDispatch();
  const { baseId } = useParams<{ baseId: string }>(); // Extract baseId from URL
  const jobApplicationData = useAppSelector(
    (state) => state.jobApplication.fetchTeamsByIdData,
  );
  const fetchStatus = useAppSelector(
    (state) => state.jobApplication.fetchTeamsByIdStatus,
  );
  const { isAddingStep, setIsAddingStep, isReorderSelection } =
    useJobApplicationContext();
  const { isFormAsideOpen, isFormAsideView, selectedElement } = useAppSelector(
    (state: RootState) => state.questionSteps,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [profileSummary, setProfileSummary] = useState(ProfileSummaryData[0]);

  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [aiTriggerCount, setAiTriggerCount] = useState(0); // Using a counter to trigger multiple times

  const handleCreateByAi = () => {
    // Incrementing this will trigger the useEffect in the child
    setAiTriggerCount((prev) => prev + 1);
  };

  // Fetch job application details on mount
  useEffect(() => {
    if (jobId && baseId) {
      setIsLoading(true); // Set loading to true before the API call
      dispatch(
        fetchJobApplication({ baseId: parseInt(baseId), id: jobId }),
      ).finally(() => {
        setIsLoading(false); // Set loading to false after the API call
      });
    }
  }, [dispatch, jobId, baseId]);

  useEffect(() => {
    if (jobApplicationData?.data?.candidate_name) {
      setName(jobApplicationData.data.candidate_name);
    }
  }, [jobApplicationData]);

  // email
  useEffect(() => {
    if (jobApplicationData?.data?.candidate_email) {
      setEmail(jobApplicationData.data.candidate_email);
    }
  }, [jobApplicationData]);

  // phonenumber
  useEffect(() => {
    if (jobApplicationData?.data?.candidate_phone_status) {
      setPhoneNumber(jobApplicationData.data.candidate_phone_status);
    }
  }, [jobApplicationData]);

  //location
  useEffect(() => {
    if (jobApplicationData?.data?.candidate_location_status) {
      setLocation(jobApplicationData.data.candidate_location_status);
    }
  }, [jobApplicationData]);

  // resume
  useEffect(() => {
    if (jobApplicationData?.data?.resume_upload_status) {
      setResume(jobApplicationData.data.resume_upload_status);
    }
  }, [jobApplicationData]);

  //cover
  useEffect(() => {
    if (jobApplicationData?.data?.cover_letter_status) {
      setCoverLetter(jobApplicationData.data.cover_letter_status);
    }
  }, [jobApplicationData]);

  //education
  useEffect(() => {
    if (jobApplicationData?.data?.education_details_status) {
      setEducationDetails(jobApplicationData.data.education_details_status);
    }
  }, [jobApplicationData]);

  // experience
  useEffect(() => {
    if (jobApplicationData?.data?.work_experience_status) {
      setWorkExperience(jobApplicationData.data.work_experience_status);
    }
  }, [jobApplicationData]);

  useEffect(() => {
    // Add smooth scrolling functionality
    const handleSmoothScroll = (e: Event) => {
      e.preventDefault();
      const target = e.target as HTMLAnchorElement;
      const id = target.getAttribute("href")?.slice(1);
      if (id) {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
          });
          setActiveSection(id);
        }
      }
    };

    const links = document.querySelectorAll('a[href^="#"]');
    links.forEach((link) => {
      link.addEventListener("click", handleSmoothScroll);
    });

    // Cleanup
    return () => {
      links.forEach((link) => {
        link.removeEventListener("click", handleSmoothScroll);
      });
    };
  }, []);

  // name
  const handleNameChange = (selectedValue) => {
    try {
      setName(selectedValue);
      dispatch(
        updateJobApplication({
          payload: {
            candidate_name:
              selectedValue === "required"
                ? "required"
                : selectedValue === "optional"
                  ? "optional"
                  : "disabled",
            job_id: jobId,
          },
          baseId: baseId ? parseInt(baseId) : 0,
          id: jobApplicationData?.data?.id,
        }),
      )
        .then(() => {
          toast.success("Name status updated successfully");
        })
        .catch((error) => {
          toast.error(error || "Failed to update name status");
        });
    } catch (error) {
      toast.error(error || "An error occurred");
    }
  };

  // email

  const handleEmailChange = (selectedValue) => {
    try {
      setEmail(selectedValue);
      dispatch(
        updateJobApplication({
          payload: {
            candidate_email:
              selectedValue === "required"
                ? "required"
                : selectedValue === "optional"
                  ? "optional"
                  : "disabled",
            job_id: jobId,
          },
          baseId: baseId ? parseInt(baseId) : 0,
          id: jobApplicationData?.data?.id,
        }),
      )
        .then(() => {
          toast.success("Email status updated successfully");
        })
        .catch((error) => {
          toast.error(error || "Failed to update email status");
        });
    } catch (error) {
      toast.error(error || "An error occurred");
    }
  };

  // phonenumber

  const handlePhoneChange = (selectedValue) => {
    try {
      setPhoneNumber(selectedValue);
      dispatch(
        updateJobApplication({
          payload: {
            candidate_phone_status:
              selectedValue === "required"
                ? "required"
                : selectedValue === "optional"
                  ? "optional"
                  : "disabled",
            job_id: jobId,
          },
          baseId: baseId ? parseInt(baseId) : 0,
          id: jobApplicationData?.data?.id,
        }),
      )
        .then(() => {
          toast.success("Phone Number status updated successfully");
        })
        .catch((error) => {
          toast.error(error || "Failed to update phone number status");
        });
    } catch (error) {
      toast.error(error || "An error occurred");
    }
  };

  // location
  const handleLocationChange = (selectedValue) => {
    try {
      setLocation(selectedValue);
      dispatch(
        updateJobApplication({
          payload: {
            candidate_location_status:
              selectedValue === "required"
                ? "required"
                : selectedValue === "optional"
                  ? "optional"
                  : "disabled",
            job_id: jobId,
          },
          baseId: baseId ? parseInt(baseId) : 0,
          id: jobApplicationData?.data?.id,
        }),
      )
        .then(() => {
          toast.success("Location status updated successfully");
        })
        .catch((error) => {
          toast.error(error || "Failed to update location status");
        });
    } catch (error) {
      toast.error(error || "An error occurred");
    }
  };

  // resume
  const handleProfileSummaryChange = (selectedValue) => {
    try {
      setProfileSummary(selectedValue);
      dispatch(
        updateJobApplication({
          payload: {
            profile_summary_status:
              selectedValue === "required"
                ? "required"
                : selectedValue === "optional"
                  ? "optional"
                  : "disabled",
            job_id: jobId,
          },
          baseId: baseId ? parseInt(baseId) : 0,
          id: jobApplicationData?.data?.id,
        }),
      )
        .then(() => {
          toast.success("Profile summary updated successfully");
        })
        .catch((error) => {
          toast.error(error || "Failed to update Profile summary");
        });
    } catch (error) {
      toast.error(error || "An error occurred");
    }
  };

  // resume
  const handleResumeChange = (selectedValue) => {
    try {
      setResume(selectedValue);
      dispatch(
        updateJobApplication({
          payload: {
            resume_upload_status:
              selectedValue === "required"
                ? "required"
                : selectedValue === "optional"
                  ? "optional"
                  : "disabled",
            job_id: jobId,
          },
          baseId: baseId ? parseInt(baseId) : 0,
          id: jobApplicationData?.data?.id,
        }),
      )
        .then(() => {
          toast.success("Resume status updated successfully");
        })
        .catch((error) => {
          toast.error(error || "Failed to update resume status");
        });
    } catch (error) {
      toast.error(error || "An error occurred");
    }
  };

  // cover
  const handleCoverChange = (selectedValue) => {
    try {
      setCoverLetter(selectedValue);
      dispatch(
        updateJobApplication({
          payload: {
            cover_letter_status:
              selectedValue === "required"
                ? "required"
                : selectedValue === "optional"
                  ? "optional"
                  : "disabled",
            job_id: jobId,
          },
          baseId: baseId ? parseInt(baseId) : 0,
          id: jobApplicationData?.data?.id,
        }),
      )
        .then(() => {
          toast.success("Cover status updated successfully");
        })
        .catch((error) => {
          toast.error(error || "Failed to update cover status");
        });
    } catch (error) {
      toast.error(error || "An error occurred");
    }
  };

  // education
  const handleEducationChange = (selectedValue) => {
    try {
      setEducationDetails(selectedValue);
      dispatch(
        updateJobApplication({
          payload: {
            education_details_status:
              selectedValue === "required"
                ? "required"
                : selectedValue === "optional"
                  ? "optional"
                  : "disabled",
            job_id: jobId,
          },
          baseId: baseId ? parseInt(baseId) : 0,
          id: jobApplicationData?.data?.id,
        }),
      )
        .then(() => {
          toast.success("Education status updated successfully");
        })
        .catch((error) => {
          toast.error(error || "Failed to update education status");
        });
    } catch (error) {
      toast.error(error || "An error occurred");
    }
  };

  // education
  const handleExperienceChange = (selectedValue) => {
    try {
      setWorkExperience(selectedValue);
      dispatch(
        updateJobApplication({
          payload: {
            work_experience_status:
              selectedValue === "required"
                ? "required"
                : selectedValue === "optional"
                  ? "optional"
                  : "disabled",
            job_id: jobId,
          },
          baseId: baseId ? parseInt(baseId) : 0,
          id: jobApplicationData?.data?.id,
        }),
      )
        .then(() => {
          toast.success("Experience status updated successfully");
        })
        .catch((error) => {
          toast.error(error || "Failed to update experience status");
        });
    } catch (error) {
      toast.error(error || "An error occurred");
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh_-_5rem)]">
        <div className="simple-spinner-loader">
          <span></span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="h-screen">
        <div className="py-[34px] lg:py-[70px] px-0 lg:px-0 h-full flex ">
          <div className="px-[0px] sm:px-0 lg:px-0 w-full flex justify-center gap-x-[3rem]">
            <div className="w-full max-w-[640px] flex gap-x-[2rem]">
              <div className="w-[100%]">
                <main className="flex-1 bg-white h-full">
                  <div className=" w-full flex justify-center">
                    <div className="w-full">
                      <div className="flex flex-col gap-y-1 w-full">
                        <h2 className="text-[20px] lg:text-[24px] font-medium leading-8 text-[#1b1b1b] tracking-[-0.01rem]">
                          Evaluation Application Form
                        </h2>
                        <p className="text-sm leading-5 text-[#5c5c5e] font-medium">
                          Customize the application form for this job. Select
                          the fields and questions you want candidates to see
                          and answer.
                        </p>
                      </div>
                      <div className="bg-[#e0e0e0] w-full h-[1px] my-6"></div>
                      <div className="mt-6 lg:mt-7 ">
                        <div
                          className="py-4 border-b border-light-gray-200  flex justify-between flex-wrap lg:flex-nowrap gap-2 mb-16"
                          id="application-detail"
                        >
                          <div className="lg:w-[100%] w-full flex flex-col gap-y-2">
                            <div className="flex justify-between">
                              <div className="flex flex-col gap-y-2">
                                <p className="text-[15px] font-medium leading-5 text-[#1b1b1b]">
                                  Application Detail
                                </p>
                                <p className="text-[0.8125rem] font-normal leading-4 text-[#5c5c5e]">
                                  Evaluation application details
                                </p>
                              </div>
                              <div>
                                <button
                                  type="button"
                                  onClick={handleCreateByAi}
                                  disabled={isAiGenerating} // Disable button while generating
                                  className="inline-flex shrink-0 justify-center rounded-md bg-indigo-600 px-[14px] py-1 text-sm font-medium leading-6 text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
                                >
                                  <div className="flex items-center gap-[6px]">
                                    {isAiGenerating
                                      ? "Generating..."
                                      : "Create by AI"}
                                    {isAiGenerating && (
                                      <div className="simple-spinner">
                                        <span></span>
                                      </div>
                                    )}
                                  </div>
                                </button>
                              </div>
                            </div>
                            <EvalutionApplicationQuestionSet
                              aiTriggerCount={aiTriggerCount}
                              setIsAiGenerating={setIsAiGenerating}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </main>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
export default EvaluationApplication;
